import { formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Injector,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DateTime } from 'luxon';
import { nextTick } from 'process';
import { ValidatorsPlus } from '../../../common/validators/plus.validator';
import { convertTimezoneToLocal, isNullOrEmpty } from '../../../core/utils';
import { DateRangeSetting, FormArrayEnhanced } from '../../../models/rt-common';
import { BaseComponent } from '../../../shared/components/base.component';
@Component({
  selector: 'sigv-input-date-range-picker',
  templateUrl: './input-date-range-picker.component.html',
  styleUrls: ['./input-date-range-picker.component.css'],
})
export class InputDateRangePickerComponent
  extends BaseComponent
  implements OnInit
{
  /** formControl物件 起始日期
   * @description useForm.controls['externalName']
   */
  @Input()
  bindingFormControlStartDate!: AbstractControl | FormArrayEnhanced | any;
  /** formControl物件 起始日期
   * @description useForm.controls['externalName']
   */
  @Input()
  bindingFormControlEndDate!: AbstractControl | FormArrayEnhanced | any;

  @Input() fieldName: string = '';
  @Input() isDateRangeRequired: boolean = false;

  /**
   * 只可選取
   */
  @Input() readonlyInput = false;

  /**
   * 移動元件至
   */
  @Input() appendTo: any;

  /**
   * 日期格式
   */
  @Input() dateFormat: any;

  /**
   * Placeholder
   */
  @Input() placeholder = '';

  /**
   * Disabled
   */
  @Input() disabled = false;

  /**
   * 頁面是否純檢視
   */
  @Input() isReadOnly = false;

  /**
   * 最小值
   */
  @Input() minDate?: Date;

  /**
   * 最大值
   */
  @Input() maxDate?: Date;

  /**
   * 年份下拉區間
   */
  @Input() yearRange?: string;

  /**
   * 顯示時間
   */
  @Input() showTime = false;

  /**
   * 分鐘 Tick
   */
  @Input() stepMinute = 1;

  /**
   * FormControlName
   */
  @Input() formControlName?: string;

  /**
   * 是否唯讀
   */
  @Input() readonly = false;

  /**
   * 大於系統日檢核
   */
  @Input() greaterThenSysDateCheck = false;

  /**
   * 小於系統日檢核
   */
  @Input() lessThenSysDateCheck = false;

  /**
   * 起迄日最大區間設定
   */
  @Input() rangeSetting?: DateRangeSetting;

  /**
   * 是否點過送出按鈕，觸發欄位下方檢核紅字
   */
  @Input() submitTouched = false;

  /**
   * 觸發檢核
   */
  @Input() forceUpdateValueAndValidity = false;

  /**
   * Blur 事件
   */
  @Output() dateBlur = new EventEmitter<{ target: string; originEvent: any }>();

  /**
   * 選取事件
   */
  @Output() dateSelect = new EventEmitter<{ target: string; value: Date }>();

  /**
   * Focus 事件
   */
  @Output() dateFocus = new EventEmitter<{
    target: string;
    originEvent: any;
  }>();

  /**
   * 輸入事件
   */
  @Output() dateInput = new EventEmitter<{
    target: string;
    originEvent: any;
  }>();
  /**
   * Clear 事件
   */
  @Output() dateClear = new EventEmitter<{
    target: string;
    originEvent: any;
  }>();

  ngDateFormat = 'MM/dd/yyyy';
  primeDateFormat: any;
  /**
   * 子元件錯誤檢核
   * @param rangeSetting
   * @returns
   */
  static dateRangeValidateInComponent(util: any): ValidatorFn | ValidatorFn[] {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (isNullOrEmpty(util?.rangeControl)) {
        return null;
      }

      const useControl = util?.rangeControl as AbstractControl;
      const errorObj = {} as any;
      if (Object.keys(useControl?.errors ?? {})?.length > 0) {
        Object.keys(useControl?.errors ?? {}).forEach((err) => {
          errorObj[err] = true;
        });
      }

      return Object.keys(errorObj).length > 0 ? errorObj : null;
    };
  }

  /**
   * 日期區間必填檢核
   *
   * @static
   * @param {AbstractControl} ctrl
   * @return {*}  {(ValidationErrors | null)}
   * @memberof DateRangeValidators
   */
  static dateRangeRequired(util: any): ValidationErrors | null {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      const val = ctrl.value as { start?: Date; end?: Date };
      const errObj = {} as any;
      if (
        isNullOrEmpty(util.rangeControl?.value?.start) &&
        isNullOrEmpty(util.rangeControl?.value?.end) === false
      ) {
        // 起日為空
        errObj.startRequired = true;
      } else if (
        isNullOrEmpty(util.rangeControl?.value?.start) === false &&
        isNullOrEmpty(util.rangeControl?.value?.end)
      ) {
        // 迄日為空
        errObj.endRequired = true;
      } else {
        return null;
      }

      return Object.keys(errObj).length === 0 ? null : errObj;
    };
  }

  model: DateRange = new DateRange();
  constructor(
    injector: Injector,
    private cd: ChangeDetectorRef,
    @Inject(LOCALE_ID) private locale: string
  ) {
    super(injector);
    this.model = this.initModel();
  }
  util = {
    rangeControl: {} as AbstractControl,
  };
  initModel(): DateRange {
    let useModel = new DateRange();
    this.logger.debug(
      'inputDatePicker',
      this.bindingFormControlStartDate?.getRawValue(),
      new Date(this.bindingFormControlStartDate?.getRawValue())
    );
    useModel.start = this.bindingFormControlStartDate?.getRawValue()
      ? DateTime.fromJSDate(this.bindingFormControlStartDate?.getRawValue())
          .isValid
        ? convertTimezoneToLocal(
            this.bindingFormControlStartDate?.getRawValue(),
            false
          )
        : null
      : null;
    useModel.end = this.bindingFormControlEndDate?.getRawValue()
      ? DateTime.fromJSDate(this.bindingFormControlEndDate?.getRawValue())
          .isValid
        ? convertTimezoneToLocal(
            this.bindingFormControlEndDate?.getRawValue(),
            false
          )
        : null
      : null;

    return useModel;
  }
  /**
   * form value改變時註冊事件
   */
  onSubscribeValue(): void {
    this.bindingFormControlStartDate.valueChanges.subscribe((value: any) => {
      if (value != null) {
        this.logger.debug('inputDatePicker onSubscribeValue start', value);
        if (typeof value === 'string') {
          this.logger.debug(
            'inputDatePicker onSubscribeValue start',
            new Date(value),
            convertTimezoneToLocal(new Date(value), false)
          );
          this.model.start = convertTimezoneToLocal(new Date(value), false);
        } else {
          this.model.start = new Date(value);
        }
      }
      if (value == null) {
        this.logger.debug('inputDatePicker onSubscribeValue start null');
        this.model.start = null;
        if (this.model.start === null && this.model.end === null) {
          this.util?.rangeControl.updateValueAndValidity();
        }
      }
    });
    this.bindingFormControlEndDate.valueChanges.subscribe((value: any) => {
      if (value != null) {
        this.logger.debug('inputDatePicker onSubscribeValue', value);
        if (typeof value === 'string') {
          this.logger.debug(
            'inputDatePicker onSubscribeValue end',
            new Date(value),
            convertTimezoneToLocal(new Date(value), false)
          );
          this.model.end = convertTimezoneToLocal(new Date(value), false);
        } else {
          this.model.end = new Date(value);
        }
      }
      if (value == null) {
        this.logger.debug('inputDatePicker onSubscribeValue end null');
        this.model.end = null;
        if (this.model.start === null && this.model.end === null) {
          this.util?.rangeControl.updateValueAndValidity();
        }
      }
    });
  }
  handleBackspacePress() {
    this.logger.debug(
      'Backspace key pressed',
      this.bindingFormControlStartDate.getRawValue()
    );
  }
  ngModelChangeEvent(): void {
    this.logger.debug(
      'ngModelChangeEvent',
      DateTime.fromJSDate(this.model.start),
      this.model.start,
      DateTime.fromJSDate(this.model.end)
    );
    this.getDateRangeRequired();
    const start = DateTime.fromJSDate(this.model.start);
    const end = DateTime.fromJSDate(this.model.end);
    if (this.model.start) {
      if ((DateTime.fromJSDate(this.model.start) as any)?.c == null) {
        this.bindingFormControlStartDate.setValue(undefined);
      } else {
        this.bindingFormControlStartDate.setValue(
          DateTime.fromJSDate(this.model.start)
        );
      }
    } else {
      this.bindingFormControlStartDate.setValue(undefined);
    }

    if (this.model.end) {
      if ((DateTime.fromJSDate(this.model.end) as any)?.c == null) {
        this.bindingFormControlEndDate.setValue(undefined);
      } else {
        this.bindingFormControlEndDate.setValue(
          DateTime.fromJSDate(this.model.end)
        );
      }
    } else {
      this.bindingFormControlEndDate.setValue(undefined);
    }
  }
  getDateRangeRequired(): boolean {
    this.logger.debug('dateRangeRequired', this.isDateRangeRequired);
    return this.isDateRangeRequired;
  }
  /**
   * 取得格式化後的日期區間字串
   *
   * @return {*}  {(string | null)}
   * @memberof DateRangePickerComponent
   */
  getFormattedDataRangeStr(): string | null {
    const ngDateFormat = this.dateFormat ?? this.envConfig.format.date;
    if (!this.model || (!this.model.start && !this.model.end)) {
      return null;
    }
    const ngFormat = this.showTime ? `${ngDateFormat} HH:mm` : ngDateFormat;
    const startStr = !this.model.start
      ? '-'
      : formatDate(this.model.start, ngFormat, this.locale);
    const endStr = !this.model.end
      ? '-'
      : formatDate(this.model.end, ngFormat, this.locale);
    const formattedStr = `${startStr} ~ ${endStr}`;

    return formattedStr;
  }
  render = true;
  forceRender() {
    this.render = false;
    nextTick(() => {
      this.render = true;
    });
  }
  onDateClear(event: any): void {
    this.dateClear.emit({ target: 'end', originEvent: event });
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isReadOnly'] || !changes['isReadOnly']) {
      this.forceRender();
    }
    if (changes['rangeSetting'] || changes['isDateRangeRequired']) {
      this.bindingFormControlStartDate.removeValidators(
        InputDateRangePickerComponent.dateRangeValidateInComponent(this.util)
      );
      this.bindingFormControlStartDate.addValidators(
        InputDateRangePickerComponent.dateRangeValidateInComponent(this.util)
      );
    }
    if (changes['greaterThenSysDateCheck']) {
      this.bindingFormControlStartDate.addValidators(
        ValidatorsPlus.dateRangeGreaterThanSysDate
      );
      this.bindingFormControlEndDate.addValidators(
        ValidatorsPlus.dateRangeGreaterThanSysDate
      );
    }
  }
  /**
   * 轉換 Primeng 日期格式至 Angular 日期格式
   *
   * @private
   * @param {string} format Primeng 日期格式
   * @return {*}  {string}
   * @memberof DateRangePickerComponent
   */
  private transformToNgFormat(format: string): string {
    let ngFormat = '';
    let regex = /yy/;
    ngFormat = format.replace(regex, 'yyyy');
    regex = /mm/;
    ngFormat = ngFormat.replace(regex, 'MM');
    return ngFormat;
  }
  /**
   * 轉換 Angular 日期格式至 Primeng 日期格式
   *
   * @private
   * @param {string} format Angular 日期格式
   * @return {*}  {string}
   * @memberof DateRangePickerComponent
   */
  private transformToPrimeFormat(format: string): string {
    let primeFormat = '';
    let regex = /yyyy/;
    primeFormat = format.replace(regex, 'yy');
    regex = /MM/;
    primeFormat = primeFormat.replace(regex, 'mm');
    return primeFormat;
  }

  ngOnInit() {
    if (isNullOrEmpty(this.dateFormat)) {
      this.primeDateFormat = this.transformToPrimeFormat(
        this.envConfig.format.date
      );
    } else {
      this.primeDateFormat = this.dateFormat!;
    }

    this.ngDateFormat = this.transformToNgFormat(this.primeDateFormat!);
    this.onSubscribeValue();
    this.cd.detectChanges();
  }
  ngAfterViewInit() {}
}

export class DateRange {
  start: any;
  end: any;
}
